import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { ringSizes } from '../../page-config';
import { CartItem } from '../../model/cartItem';
import { Product } from '../../model/product';
import { AuthService } from '../../services/auth.service';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { HeadService } from '../../services/head.service';
import { FavouritesService } from '../../services/favourites.service';
import { ProductService } from '../../services/product.service';
import { TitleService } from '../../services/title.service';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { finalize, share, tap } from 'rxjs/operators';
import { BreadCrumbComponent } from '../breadcrumb/breadcrumb.component';
import { ImgSrcSetDirective } from '../../directives/img-src-set.directive';
import { NgClass, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ThumbnailComponent } from '../thumbnail/thumbnail.component';

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    imports: [BreadCrumbComponent, RouterLink, ImgSrcSetDirective, NgClass, FormsModule, ThumbnailComponent, AsyncPipe]
})
export class ProductComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private breadcrumbService = inject(BreadcrumbService);
  private headService = inject(HeadService);
  private productService = inject(ProductService);
  private titleService = inject(TitleService);
  auth = inject(AuthService);
  shoppingCartService = inject(ShoppingCartService);
  favouritesService = inject(FavouritesService);


  product$: Observable<Product>;
  cachedProduct$: Observable<Product>;
  product: Product;

  ringSizes: Array<string> = ringSizes.ireland;
  ringSize = 'M';

  subscription: Subscription;
  productSubscription: Subscription;

  ngOnInit(): void {
    this.subscription = this.route.params.subscribe(routeParams => {
      this.product$ = this.fetchCachedProduct(routeParams);
      this.productSubscription = this.product$.subscribe(product => {
        this.titleService.set(product.title);
        this.breadcrumbService.setGallery(product.collection, product.type);
        this.headService.createCanonicalLink(product.safeProductUrl);
        this.headService.setMetaDescription(`${product.title} - ${product.description}`);
        this.headService.setProductStructuredData(product);
        this.headService.setFacebookMetaTags(product);
        this.headService.setTwitterMetaTags(product);
      }, () => {
        this.router.navigate(['/404']);
      });
    });
    this.favouritesService.load();
  }

  fetchProduct(routeParams): Observable<Product> {
    if(routeParams.slug) {
      console.log('Fetching product by slug');
      return this.productService.fetch(routeParams.slug);
    } else if(routeParams.code) {
      console.log('Fetching product by code');
      return this.productService.fetchByCode(routeParams.code);
    }
  }

  fetchCachedProduct(routeParams): Observable<Product> {
    let observable: Observable<Product>;
    if (this.product) {
      observable = of(this.product);
    }  else if (this.cachedProduct$) {
      observable = this.cachedProduct$;
    } else {
      this.cachedProduct$ = this.fetchProduct(routeParams)
        .pipe(
          tap(res => this.product = res),
          share(),
          finalize(() => this.cachedProduct$ = null)
        );
        observable = this.cachedProduct$;
    }
    return observable;
  }

  ngOnDestroy(): void {
    if(this.subscription) {
      this.subscription.unsubscribe();
    }
    if(this.productSubscription) {
      this.productSubscription.unsubscribe();
    }
  }

  addToCart(product: Product): void {
    const cartItem = new CartItem(product.code, product);
    if(product.isRing) {
      cartItem.ringSize = this.ringSize;
    }
    this.shoppingCartService.addToCart(cartItem);
  }
}
